import { Callout, Intent } from '@blueprintjs/core';
import * as React from 'react';

import { getPublishProjectDrawerNamedGraphDeletionWarningTestId } from 'src/constants/testIds';
import {
  ModelAliasInfo,
  ModelsInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { dataModelItemsSelector } from 'src/selectors/dataModelItems';
import { namedGraphsSelector } from 'src/selectors/namedGraphs';
import * as copy from 'src/templates/copy';
import { getDanglingNamedGraphs } from 'src/utils/getDanglingNamedGraphs';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export type NamedGraphDeletionWarningProps = {
  databaseId: string;
  filterModelNames?: string[];
  modelAliasInfo?: ModelAliasInfo;
  modelsInfo: ModelsInfo;
};

/**
 * Displays when the user has chosen to replace a Data Model,
 * and change the named graph iri associated with it.
 *
 * Also displayed when the user modifies a Combo Model to
 * create a dangling named graph iri.
 *
 * We only delete named graphs of "Data Models".
 * (ie models with a single named graph associated)
 */
export const NamedGraphDeletionWarning: React.VFC<
  NamedGraphDeletionWarningProps
> = ({ databaseId, filterModelNames, modelAliasInfo, modelsInfo }) => {
  const dataModelItems = useReduxSelector(dataModelItemsSelector(databaseId));
  const namedGraphs = useReduxSelector(namedGraphsSelector(databaseId));

  const danglingNamedGraphIriByModel = getDanglingNamedGraphs({
    dataModelItems,
    modelsInfo,
    modelAliasInfo,
    namedGraphs,
  });

  return (
    <>
      {Object.entries(danglingNamedGraphIriByModel)
        .filter(
          ([modelName]) =>
            !filterModelNames || filterModelNames?.includes(modelName)
        )
        .map(([modelName, namedGraphIri], idx) => (
          <Callout
            key={modelName}
            data-testid={getPublishProjectDrawerNamedGraphDeletionWarningTestId(
              modelName,
              namedGraphIri,
              idx
            )}
            intent={Intent.WARNING}
          >
            {copy.components.publishProjectDrawer.willDelete({
              modelName,
              namedGraphIri,
            })}
          </Callout>
        ))}
    </>
  );
};
